// Copyright (c) Microsoft. All rights reserved.

import * as React from 'react';
import debug from 'debug';
import { Constants } from '../../../Constants';
import {
    Button,
    Checkbox,
    Input,
    Popover,
    PopoverTrigger,
    PopoverSurface,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableColumnDefinition,
    TableHeader,
    TableHeaderCell,
    TableRow,
    createTableColumn,
    makeStyles,
    shorthands,
    tokens,
    mergeClasses,
} from '@fluentui/react-components';

import {
    CheckboxChecked20Regular,
    CheckboxUnchecked20Regular,
    Info16Regular,
    Eraser20Regular,
    Search16Regular,
} from '@fluentui/react-icons';
import { TabView } from './TabView';

import { IArchiveSources } from '../../../libs/models/ArchiveSources';
import { useAppDispatch, useAppSelector } from '../../../redux/app/hooks';
import { useChat } from '../../../libs/hooks/useChat';
import { RootState } from '../../../redux/app/store';
import { Alerts } from '../../shared/Alerts';
import { addAlert, removeAlertsWithMetadata } from '../../../redux/features/app/appSlice';
import { AlertType } from '../../../libs/models/AlertType';
import { editConversationArchiveSources } from '../../../redux/features/conversations/conversationsSlice';
import { AlertMetadata } from '../../../redux/features/app/AppState';
import { IDataTypeBase, dataTypeStyles } from '../../shared/IDataTypeBase';

const log = debug(Constants.debug.root).extend('archive-tab');

const useClasses = makeStyles({
    buttons: {
        display: 'flex',
        ...shorthands.gap('20px'),
        ...shorthands.margin('10px', '0'),
    },
    table: {
        backgroundColor: tokens.colorNeutralBackground1,
    },
    tableHeader: {
        height: '80px',
    },
    searchInput: {
        width: '100%',
        maxWidth: '300px',
        height: '40px',
    },
    gappedText: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('10px'),
    },
    tagButtonContainer: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('10px'),
        ...shorthands.padding('10px', '10px'),
    },
    lessPadding: {
        ...shorthands.padding('0', '10px'),
    },
    tagButtons: {
        display: 'flex',
        flexWrap: 'wrap',
        fontWeight: 'bold',
        ...shorthands.textDecoration('blink'),
    },
});

interface IDataType extends IDataTypeBase {
    index: number;
    checked: boolean;
}

interface ITableItem {
    index: number;
    platformName: string;
    checked: 'mixed' | boolean;
    dataTypes: IDataType[];
    description: string;
    platformNameColor?: string;
}

export const ArchiveSourcesTab: React.FC = () => {
    const { archiveSourceAvailability, alerts } = useAppSelector((state: RootState) => state.app);
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const chatState = conversations[selectedId];
    const databaseArchiveSources = chatState.selectedSources;

    const classes = useClasses();
    const dispatch = useAppDispatch();

    const chat = useChat();
    const [stateArchiveSources, setStateArchiveSources] = React.useState<ITableItem[]>([]);
    const [globalDataTypeSelections, setGlobalDataTypeSelections] = React.useState<IDataType[]>([]);
    const [selectNone, setSelectNone] = React.useState(false);

    const updateConversationArchiveSources = async (currentSources: ITableItem[]) => {
        const selectedSources: IArchiveSources[] = [];
        let everythingSelected = true;

        for (const [_index, source] of currentSources.entries()) {
            const entry: IArchiveSources = {
                PlatformName: source.platformName,
                DataTypes: [],
                Description: source.description,
                PlatformNameColor: source.platformNameColor,
            };
            if (source.checked) {
                source.dataTypes.forEach((dataType) => {
                    if (dataType.checked) {
                        entry.DataTypes.push(dataType.name);
                    } else {
                        everythingSelected = false;
                    }
                });
                if (entry.DataTypes.length > 0) {
                    selectedSources.push(entry);
                }
            } else {
                everythingSelected = false;
            }
        }

        const updatedSelectedSources = everythingSelected ? [] : selectedSources;

        await chat.editChat(
            selectedId,
            chatState.title,
            chatState.memoryBalance,
            chatState.chatScope,
            chatState.isHidden,
            updatedSelectedSources,
        );

        dispatch(
            editConversationArchiveSources({
                id: selectedId,
                newArchiveSources: updatedSelectedSources,
            }),
        );
    };

    const onChangeSelectNone = (newValue: boolean) => {
        const currentValue = selectNone;
        if (!currentValue && newValue) {
            dispatch(
                addAlert({
                    type: AlertType.Info,
                    hideDismissButton: true,
                    metadata: AlertMetadata.NoArchiveSourcesSelected,
                    message:
                        'No archive sources have been selected, so all archive sources will be searched. If you intend to limit the scope of your conversation to one or more archive sources, please select them as appropriate.',
                }),
            );
        }
        if (currentValue && !newValue) {
            dispatch(removeAlertsWithMetadata(AlertMetadata.NoArchiveSourcesSelected));
        }
    };

    const onSelectAll = (checked: boolean) => {
        const currentSources: ITableItem[] = [...stateArchiveSources];
        onChangeSelectNone(!checked);
        currentSources.forEach((platform) => {
            platform.checked = checked;
            platform.dataTypes.forEach((dataType) => {
                dataType.checked = true;
            });
        });

        updateConversationArchiveSources(currentSources).catch((err) => {
            log('An error occured whilst editing chat state', err);
        });
    };

    const checkboxSelect = (event: React.MouseEvent<HTMLElement>, index: number) => {
        event.preventDefault(); // ensures event does not get triggered twice when checkbox label is clicked
        const currentSources: ITableItem[] = [...stateArchiveSources];
        currentSources[index].checked = !currentSources[index].checked;
        if (currentSources[index].checked) {
            currentSources[index].dataTypes.forEach((dataType) => {
                dataType.checked = true;
            });
        }
        setStateArchiveSources(currentSources);
        const totalSelectedSources = currentSources.filter((row) => row.checked).length;
        totalSelectedSources === 0 ? onChangeSelectNone(true) : onChangeSelectNone(false);
        updateConversationArchiveSources(currentSources).catch((err) => {
            log('An error occured whilst editing chat state', err);
        });
    };

    const globalDataTypeSelect = (keyIndex: number) => {
        const currentGlobalDataTypeSelect: IDataType[] = [...globalDataTypeSelections];
        const index = currentGlobalDataTypeSelect.findIndex((d) => d.index === keyIndex);

        if (index === -1) {
            return;
        }

        const checked = !currentGlobalDataTypeSelect[index].checked;
        const currentSources = [...stateArchiveSources];

        currentGlobalDataTypeSelect[index].checked = checked;

        for (const [_plIndex, platform] of currentSources.entries()) {
            for (const [_dtIndex, dataType] of platform.dataTypes.entries()) {
                if (dataType.name === currentGlobalDataTypeSelect[index].name) {
                    currentSources[_plIndex].dataTypes[_dtIndex].checked = checked;
                }
            }
        }

        for (const [_plIndex, platform] of currentSources.entries()) {
            let plChecked: boolean | 'mixed' = false;
            if (platform.dataTypes.filter((p) => p.checked).length === platform.dataTypes.length) {
                plChecked = true;
            } else if (platform.dataTypes.filter((p) => p.checked).length > 0) {
                plChecked = 'mixed';
            }
            currentSources[_plIndex].checked = plChecked;
        }

        setGlobalDataTypeSelections(currentGlobalDataTypeSelect);
        setStateArchiveSources(currentSources);
        const totalSelectedSources = currentSources.filter((row) => row.checked).length;
        totalSelectedSources === 0 ? onChangeSelectNone(true) : onChangeSelectNone(false);
        updateConversationArchiveSources(currentSources).catch((err) => {
            log('An error occured whilst editing chat state', err);
        });
    };

    const dataTypeSelect = (platformIndex: number, datatypeIndex: number) => {
        const currentSources: ITableItem[] = [...stateArchiveSources];
        currentSources[platformIndex].dataTypes[datatypeIndex].checked =
            !currentSources[platformIndex].dataTypes[datatypeIndex].checked;

        const checkedDataTypes = currentSources[platformIndex].dataTypes.filter((d) => d.checked);
        if (checkedDataTypes.length == currentSources[platformIndex].dataTypes.length) {
            currentSources[platformIndex].checked = true;
        } else if (checkedDataTypes.length > 0) {
            currentSources[platformIndex].checked = 'mixed';
        } else {
            currentSources[platformIndex].checked = false;
        }
        const totalSelectedSources = currentSources.filter((row) => row.checked).length;
        totalSelectedSources === 0 ? onChangeSelectNone(true) : onChangeSelectNone(false);
        updateConversationArchiveSources(currentSources).catch((err) => {
            log('An error occured whilst editing chat state', err);
        });
    };

    React.useEffect(() => {
        const stylizeDataTypes = (dataTypes: string[]): IDataType[] => {
            const stylizedDataTypes: IDataType[] = [];
            let index = -1;
            dataTypes.forEach((item) => {
                index++;
                let background = '#000000';
                let text = '#ffffff';
                const styleIndex = dataTypeStyles.findIndex((i) => i.name.toLowerCase() === item.toLowerCase());
                if (styleIndex > -1) {
                    background = dataTypeStyles[styleIndex].background;
                    text = dataTypeStyles[styleIndex].text;
                }
                stylizedDataTypes.push({ index, name: item, background, text, checked: false });
            });
            return stylizedDataTypes;
        };

        if (!archiveSourceAvailability || !archiveSourceAvailability.loaded) {
            return;
        }

        let buildArchiveSources: ITableItem[] = [];
        let tblIndex = -1;

        archiveSourceAvailability.Sources.forEach((source: IArchiveSources) => {
            tblIndex++;
            buildArchiveSources.push({
                index: tblIndex,
                checked: false,
                platformName: source.PlatformName,
                dataTypes: stylizeDataTypes(source.DataTypes),
                description: source.Description,
                platformNameColor: source.PlatformNameColor,
            });
        });

        if (!selectNone) {
            if (typeof databaseArchiveSources === 'undefined' || databaseArchiveSources.length == 0) {
                if (stateArchiveSources.length) {
                    buildArchiveSources = [...stateArchiveSources];
                } else {
                    buildArchiveSources.forEach((platform) => {
                        platform.checked = true;
                        platform.dataTypes.forEach((dataType) => {
                            dataType.checked = true;
                        });
                    });
                }
            } else {
                for (const [plIndex, buildSource] of buildArchiveSources.entries()) {
                    let plCheckbox: boolean | 'mixed' = false;
                    const dbIndex = databaseArchiveSources.findIndex(
                        (entry) => entry.PlatformName === buildSource.platformName,
                    );
                    if (
                        dbIndex > -1 &&
                        databaseArchiveSources[dbIndex].DataTypes.length == buildSource.dataTypes.length
                    ) {
                        plCheckbox = true;
                        buildSource.dataTypes.forEach((dt) => (dt.checked = true));
                    } else if (dbIndex > -1) {
                        plCheckbox = 'mixed';
                        buildSource.dataTypes.forEach(
                            (dt) =>
                                (dt.checked =
                                    databaseArchiveSources[dbIndex].DataTypes.findIndex((d) => d === dt.name) > -1),
                        );
                    }
                    buildArchiveSources[plIndex].checked = plCheckbox;
                }
            }
        }

        let glbDsIndex = -1;
        const buildGlobalDataTypeSelections: IDataType[] = [];
        for (const [_pIndex, source] of buildArchiveSources.entries()) {
            for (const [_dsIndex, dataType] of source.dataTypes.entries()) {
                const currIndex = buildGlobalDataTypeSelections.findIndex((g) => g.name === dataType.name);
                if (currIndex === -1) {
                    glbDsIndex++;
                    buildGlobalDataTypeSelections.push({
                        index: glbDsIndex,
                        name: dataType.name,
                        background: dataType.background,
                        text: dataType.text,
                        checked: selectNone ? false : dataType.checked,
                    });
                } else if (!buildGlobalDataTypeSelections[currIndex].checked && dataType.checked) {
                    buildGlobalDataTypeSelections[currIndex].checked = true;
                }
            }
        }

        buildGlobalDataTypeSelections.sort((a, b) => {
            const nameA = a.name.toLowerCase();
            const nameB = b.name.toLowerCase();
            if (nameA < nameB) {
                return -1;
            }
            if (nameA > nameB) {
                return 1;
            }
            return 0;
        });

        setGlobalDataTypeSelections(buildGlobalDataTypeSelections);
        setStateArchiveSources(buildArchiveSources);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectNone, archiveSourceAvailability, databaseArchiveSources]);

    React.useEffect(() => {
        if (alerts.filter((a) => a.metadata === AlertMetadata.NoArchiveSourcesSelected).length > 0) {
            setSelectNone(true);
        } else {
            setSelectNone(false);
        }
    }, [alerts]);

    const { columns, rows } = useTable(
        useClasses,
        stateArchiveSources,
        checkboxSelect,
        dataTypeSelect,
        globalDataTypeSelect,
        globalDataTypeSelections,
    );

    return (
        <>
            {archiveSourceAvailability && !archiveSourceAvailability.loaded && <p>Loading...</p>}

            {archiveSourceAvailability &&
                archiveSourceAvailability.loaded &&
                archiveSourceAvailability.Sources.length > 0 && (
                    <TabView title="Archive Sources" learnMoreDescription="" learnMoreLink="">
                        <div className={classes.gappedText}>
                            <span>Select one or more data sources you would like to search.</span>
                            <span>Use the buttons below to select/unselect all archive sources.</span>
                        </div>

                        <div>
                            <div className={classes.buttons}>
                                <Button
                                    appearance="outline"
                                    icon={<CheckboxChecked20Regular />}
                                    onClick={() => {
                                        onSelectAll(true);
                                    }}
                                    disabled={
                                        databaseArchiveSources && databaseArchiveSources.length === 0 && !selectNone
                                            ? true
                                            : false
                                    }
                                >
                                    Select All
                                </Button>
                                <Button
                                    appearance="outline"
                                    icon={<CheckboxUnchecked20Regular />}
                                    onClick={() => {
                                        onSelectAll(false);
                                    }}
                                    disabled={selectNone}
                                >
                                    Select None
                                </Button>
                            </div>
                        </div>

                        <div>
                            <Alerts showWithMetadata={AlertMetadata.NoArchiveSourcesSelected} />
                        </div>
                        <Table aria-label="Archive Sources selection table" className={classes.table}>
                            <TableHeader>
                                <TableRow>{columns.map((column) => column.renderHeaderCell())}</TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((item) => (
                                    <TableRow
                                        key={`row-${item.index}`}
                                        style={{
                                            backgroundColor: item.checked ? tokens.colorNeutralBackground2Selected : '',
                                            display: 'table-row',
                                        }}
                                    >
                                        {columns.map((column) => column.renderCell(item))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TabView>
                )}
        </>
    );
};

function useTable(
    useClasses: () => Record<string, string>,
    sources: ITableItem[],
    selectCheckbox: (event: any, index: number) => void = () => {},
    selectDataType: (platformIndex: number, dataTypeIndex: number) => void = () => {},
    selectGlobalDataType: (dataTypeIndex: number) => void = () => {},
    globalDataTypeSelections: IDataType[],
) {
    const [searchText, setSearchText] = React.useState<string>('');
    const searchField = React.useId();
    const classes = useClasses();

    interface IPopoverContentProps {
        platformName: string;
        description: string;
    }

    const PopoverContent: React.FC<IPopoverContentProps> = (props) => {
        return (
            <div>
                <h3 style={{ margin: 0 }}>{props.platformName}</h3>
                <div>{props.description}</div>
            </div>
        );
    };

    const columns: Array<TableColumnDefinition<ITableItem>> = [
        createTableColumn<ITableItem>({
            columnId: 'source',
            renderHeaderCell: () => (
                <TableHeaderCell key="search" className={classes.tableHeader}>
                    <>
                        <Input
                            contentBefore={<Search16Regular />}
                            id={searchField}
                            className={classes.searchInput}
                            placeholder="Search for an archive source"
                            value={searchText}
                            onChange={(e) => {
                                setSearchText(e.target.value);
                            }}
                        />
                        <Button
                            size="large"
                            icon={<Eraser20Regular />}
                            onClick={() => {
                                setSearchText('');
                            }}
                        />
                    </>
                </TableHeaderCell>
            ),
            renderCell: (item) =>
                searchText === '' || item.platformName.toLowerCase().includes(searchText.toLowerCase()) ? (
                    <TableCell
                        key={`platformName-${item.index}`}
                        onClick={(e) => {
                            selectCheckbox(e, +item.index);
                        }}
                    >
                        <TableCellLayout>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Checkbox
                                    checked={item.checked}
                                    key={`checkbox-${item.index}`}
                                    label={item.platformName}
                                    style={{ fontWeight: 'bold' }}
                                />
                                <Popover withArrow openOnHover>
                                    <PopoverTrigger disableButtonEnhancement>
                                        <Info16Regular />
                                    </PopoverTrigger>
                                    <PopoverSurface tabIndex={-1}>
                                        <PopoverContent
                                            platformName={item.platformName}
                                            description={item.description}
                                        />
                                    </PopoverSurface>
                                </Popover>
                            </div>
                        </TableCellLayout>
                    </TableCell>
                ) : (
                    ''
                ),
        }),
        createTableColumn<ITableItem>({
            columnId: 'dataTypes',
            renderHeaderCell: () => (
                <TableHeaderCell key="event-type-select">
                    <div className={classes.tagButtonContainer}>
                        <span style={{ margin: '0 5px 0 5px' }}>
                            Use the buttons below to select/unselect the relevant data type(s) that you wish to use.
                        </span>
                        <div className={classes.tagButtons}>
                            {globalDataTypeSelections.map((dataType: IDataType) => (
                                <span
                                    style={{
                                        opacity: dataType.checked ? 1 : 0.2,
                                        backgroundColor: dataType.background,
                                        color: dataType.text,
                                        padding: '5px',
                                        width: '100px',
                                        margin: '5px',
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                    }}
                                    key={`glbDataType-${dataType.index}}`}
                                    onClick={() => {
                                        selectGlobalDataType(dataType.index);
                                    }}
                                >
                                    {dataType.name}
                                </span>
                            ))}
                        </div>
                    </div>
                </TableHeaderCell>
            ),
            renderCell: (item) =>
                searchText === '' || item.platformName.toLowerCase().includes(searchText.toLowerCase()) ? (
                    <TableCell key={`dataTypes-${item.index}`}>
                        <TableCellLayout>
                            <div className={mergeClasses(classes.tagButtonContainer, classes.lessPadding)}>
                                <div className={classes.tagButtons}>
                                    {item.dataTypes.map((dataType) => (
                                        <span
                                            style={{
                                                opacity: item.checked ? (dataType.checked ? 1 : 0.2) : 0.2,
                                                backgroundColor: dataType.background,
                                                color: dataType.text,
                                                padding: '5px',
                                                width: '100px',
                                                margin: '5px',
                                                borderRadius: '5px',
                                                cursor: 'pointer',
                                                textAlign: 'center',
                                            }}
                                            key={`dataType-${dataType.index}}`}
                                            onClick={() => {
                                                selectDataType(item.index, dataType.index);
                                            }}
                                        >
                                            {dataType.name}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </TableCellLayout>
                    </TableCell>
                ) : (
                    ''
                ),
        }),
    ];

    const items: ITableItem[] = sources.map((item, index) => {
        return {
            index: index,
            platformName: item.platformName,
            dataTypes: item.dataTypes,
            description: item.description,
            platformNameColor: item.platformNameColor,
            checked: item.checked,
        };
    });

    return { columns, rows: items };
}
